import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Box,
  Button,
  Card,
  CircularProgress,
  FormLabel,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AlignIcon,
  DeleteIcon,
  SuccessIcon,
  WarningFilledIcon,
} from '@sharedCR/coreDesignSystem/icons';
import { documentApi } from 'app/services/api';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import ScrollWrapper from 'app/shared/components/common/ScrollWrapper';
import clsx from 'clsx';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconDirection, IconSize } from 'constants/coreDesignSystem';
import { DOCUMENT_FILE_MIME_TYPE, DOCUMENT_FILE_TYPE } from 'constants/file';
import { PromiseStatus } from 'constants/promise';
import { compressFile } from 'utils/file';

import { getDocumentsAction } from 'store/documents/actions';
import {
  closeDialogAction,
  showMessageAction,
  updateDialogOptionsAction,
} from 'store/global/actions';

import { DocumentTestIds } from '../document.testIds';

import { HTTP_STATUS_OK } from './UploadDocumentsModal.constants';
import { getUploadDocumentsModalStyles } from './UploadDocumentsModal.styles';
import { getFilesForAdding, getFulfilledFiles, getMessageText } from './UploadDocumentsModal.utils';

const defaultArray = [];
const defaultObject = {};

const UploadDocumentsModal = ({
  loanParams = defaultObject,
  documentTypeId,
  documentTypeName = null,
  dialogId = null,
  isModal = true,
  missingUploadedFiles = defaultArray,
  updateMissingDocumentTypes = null,
  isMultiple = true,
  uploadFile: passedUploadFileFunction,
  uploadFileAllowedFormats = [DOCUMENT_FILE_TYPE.PDF],
  abortController,
  formRef = null,
  uploadButtonText = 'Upload',
  customStyles = defaultObject,
}) => {
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState(missingUploadedFiles);
  const [isDragEnter, setIsDragEnter] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const uploadedListRef = useRef();
  const listRef = useRef();

  const fileInputRef = useRef(null);

  const validFormats = uploadFileAllowedFormats.filter(format => !_.isNil(format));
  const fileInputAllowedMimeTypes = validFormats
    .map(format => DOCUMENT_FILE_MIME_TYPE[format])
    .filter(mime => !_.isNil(mime));
  const fileInputMimeAccept = fileInputAllowedMimeTypes.join(', ');
  const fileTypeName = validFormats.join(', ');

  const theme = useTheme();

  const classes = getUploadDocumentsModalStyles(theme, {
    isDragEnter,
    isModal,
    customStyles,
  });
  const dispatch = useDispatch();

  const clearFileInput = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  useEffect(() => {
    setFiles([]);
    setUploadedFiles(missingUploadedFiles);
    clearFileInput();
  }, [documentTypeId, clearFileInput, missingUploadedFiles]);

  useEffect(() => {
    if (!dialogId || !documentTypeName) {
      return;
    }

    dispatch(
      updateDialogOptionsAction(dialogId, {
        header: `Upload — ${documentTypeName}`,
      }),
    );
  }, [dispatch, dialogId, documentTypeName]);

  const uploadFile = useCallback(
    file =>
      compressFile(file)
        .then(file => {
          const fd = new FormData();
          fd.append('file', file, file.name);
          const params = {
            ...loanParams,
            formData: fd,
            documentTypeId,
            signal: abortController.signal,
          };

          return documentApi.createDocumentV2(params);
        })
        .catch(({ message }) => dispatch(showMessageAction({ message, variant: 'error' }))),
    [loanParams, documentTypeId, dispatch, abortController],
  );

  const onCheckFilesError = useCallback(
    (invalidFilesNames, alreadyAddedFilesNames, alreadyUploadedFilesNames) => {
      const invalidMessage = getMessageText(invalidFilesNames, 'invalid');
      const alreadyAddedMessage = getMessageText(alreadyAddedFilesNames, 'already added');
      const alreadyUploadedMessage = getMessageText(alreadyUploadedFilesNames, 'already uploaded');

      dispatch(
        showMessageAction({
          message: _.without(
            [invalidMessage, alreadyAddedMessage, alreadyUploadedMessage],
            undefined,
          ),
          variant: 'error',
        }),
      );
    },
    [dispatch],
  );

  const onFileInputChange = useCallback(
    e => {
      const { files: newFiles } = e.target;

      if (newFiles) {
        const filesForAdding = getFilesForAdding(
          newFiles,
          files,
          getFulfilledFiles(uploadedFiles),
          onCheckFilesError,
          fileInputAllowedMimeTypes,
        );

        setFiles(_.concat(isMultiple ? files : [], filesForAdding));
        clearFileInput();
      }
    },
    [
      onCheckFilesError,
      clearFileInput,
      files,
      uploadedFiles,
      isMultiple,
      fileInputAllowedMimeTypes,
    ],
  );

  const onRemoveFile = e => {
    e.preventDefault();
    const { name } = e.target;

    if (name) {
      setFiles(prevFiles => prevFiles.filter(file => file.name !== name));
      clearFileInput();
    }
  };

  const onClearClick = useCallback(
    e => {
      e.preventDefault();

      setFiles([]);
      clearFileInput();
    },
    [clearFileInput],
  );

  const onDragEnter = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragEnter(true);
  }, []);

  const onDragOver = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const onDragLeaveHandler = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();

    if (e.target === e.currentTarget) {
      setIsDragEnter(false);
    }
  }, []);

  const onDropFile = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();

      setIsDragEnter(false);

      const dt = e.dataTransfer;

      if (dt.files) {
        const filesForAdding = getFilesForAdding(
          dt.files,
          files,
          getFulfilledFiles(uploadedFiles),
          onCheckFilesError,
          fileInputAllowedMimeTypes,
        );

        setFiles(_.concat(isMultiple ? files : [], filesForAdding));
        clearFileInput();
      }
    },
    [
      onCheckFilesError,
      clearFileInput,
      files,
      uploadedFiles,
      isMultiple,
      fileInputAllowedMimeTypes,
    ],
  );

  const onUploadFiles = useCallback(
    async e => {
      e.preventDefault();

      if (!files.length) {
        return;
      }

      const fileHandler = passedUploadFileFunction || uploadFile;
      const uploadQueue = files.map(fileHandler);

      try {
        setIsUploading(true);

        const result = await (isMultiple
          ? Promise.allSettled(uploadQueue)
          : Promise.all(uploadQueue));

        if (!result?.[0]) {
          return;
        }

        const uploadedFiles = result.map(({ value, reason, status }, index) => ({
          status:
            value?.status === HTTP_STATUS_OK ||
            (status === PromiseStatus.FULFILLED && value?.payload?.variant !== 'error')
              ? PromiseStatus.FULFILLED
              : PromiseStatus.REJECTED,
          value,
          reason,
          name: files[index].name,
        }));

        updateMissingDocumentTypes?.(
          documentTypeId,
          uploadedFiles,
          !!uploadedFiles.find(({ status }) => status === PromiseStatus.FULFILLED),
        );

        setUploadedFiles(prevUploadedFiles =>
          _.uniqBy(prevUploadedFiles.concat(...uploadedFiles), 'name'),
        );

        setFiles([]);
        clearFileInput();

        if (isMultiple) {
          dispatch(getDocumentsAction(loanParams));
        }

        if (!isMultiple) {
          dispatch(closeDialogAction());
        }

        // TODO: response data should be written in redux store, when documents list component will be ready
      } catch {
        // do nothing, coz errors already have been processed by Promise.allSettled
      } finally {
        setIsUploading(false);
      }
    },
    [
      clearFileInput,
      dispatch,
      documentTypeId,
      files,
      loanParams,
      updateMissingDocumentTypes,
      uploadFile,
      isMultiple,
      passedUploadFileFunction,
    ],
  );

  const onPreventClick = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onKeyPress = useCallback(e => {
    e.preventDefault();
  }, []);

  const renderError = reason => {
    if (!reason) {
      return null;
    }

    const errorText = reason?.response?.data || reason.message || 'Unexpected error :(';

    return (
      <Typography
        className="pt-2"
        data-testid={DocumentTestIds.DOCUMENTS_UPLOAD_ERROR_TEXT_LABEL}
        sx={classes.errorText}
        variant="medium12"
      >
        {errorText}
      </Typography>
    );
  };

  const renderIcon = (fileName, status = PromiseStatus.PENDING) => {
    switch (status) {
      case PromiseStatus.PENDING:
        if (isUploading) {
          return (
            <CircularProgress
              color="inherit"
              data-testid={DocumentTestIds.DOCUMENTS_UPLOAD_SPINNER}
              size={24}
            />
          );
        }

        return (
          <ButtonWithTooltip
            data-testid={DocumentTestIds.DOCUMENTS_UPLOAD_DELETE_BUTTON}
            icon={DeleteIcon}
            iconButtonClassName={clsx(classes.removeFileButton, 'text-white')}
            iconClassName="tool__disable-interaction"
            iconSize={IconSize[24]}
            name={fileName}
            size="small"
            title="Delete"
            variant="outlined"
            onClick={onRemoveFile}
          />
        );

      case PromiseStatus.FULFILLED:
        return (
          <Box
            component={SuccessIcon}
            data-testid={DocumentTestIds.DOCUMENTS_UPLOAD_SUCCESS_ICON}
            size={IconSize[24]}
            sx={classes.successIcon}
          />
        );

      case PromiseStatus.REJECTED:
        return (
          <WarningFilledIcon
            data-testid={DocumentTestIds.DOCUMENTS_UPLOAD_ERROR_ICON}
            size={IconSize[24]}
            style={classes.errorText}
          />
        );

      default:
        return null;
    }
  };

  const renderFileCard = (fileName, status, reason) => {
    return (
      <div className="flex justify-between items-center w-full p-12">
        <div>
          <Typography className="text-white" variant="medium14">
            {fileName}
          </Typography>
          {renderError(reason)}
        </div>
        {renderIcon(fileName, status)}
      </div>
    );
  };

  return (
    <Box className="flex flex-col justify-center p-24" sx={classes.formWrapper}>
      <Box className="mb-8" sx={classes.cardsWrapper}>
        <ScrollWrapper isDragging={false} scrollRef={uploadedListRef}>
          {uploadedFiles.map(({ name, status, reason }) => (
            <Card
              className="w-full mb-16 rounded-4"
              data-testid={DocumentTestIds.DOCUMENTS_UPLOAD_UPLOADED_FILE_CARD}
              elevation={2}
              key={name}
              sx={classes.fileCard}
            >
              {renderFileCard(name, status, reason)}
            </Card>
          ))}
        </ScrollWrapper>
      </Box>

      <form
        noValidate
        className="flex flex-col justify-center"
        data-ischanged={!!files.length}
        id="document-upload-form"
        ref={formRef}
        onKeyPress={onKeyPress}
      >
        <input
          accept={fileInputMimeAccept}
          className="tool__visually-hidden"
          data-testid={DocumentTestIds.DOCUMENTS_UPLOAD_FILE_INPUT}
          id="document-upload"
          multiple={isMultiple}
          ref={fileInputRef}
          style={classes.fileInput}
          type="file"
          onChange={onFileInputChange}
        />

        <FormLabel
          data-testid={DocumentTestIds.DOCUMENTS_UPLOAD_DROPZONE}
          htmlFor="document-upload"
          sx={classes.label}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeaveHandler}
          onDragOver={onDragOver}
          onDrop={onDropFile}
        >
          <Box sx={classes.cardsWrapper}>
            <ScrollWrapper isDragging={false} scrollRef={listRef}>
              {files.map(({ name }) => (
                <Card
                  className="w-full mb-16 rounded-4"
                  data-testid={DocumentTestIds.DOCUMENTS_UPLOAD_SELECTED_FILE_CARD}
                  elevation={2}
                  key={name}
                  sx={classes.fileCard}
                  onClick={onPreventClick}
                >
                  {renderFileCard(name)}
                </Card>
              ))}
            </ScrollWrapper>
          </Box>
          <div
            className={clsx(
              classes.dndTip,
              'flex flex-col justify-center items-center tool__disable-interaction',
            )}
          >
            <AlignIcon direction={IconDirection.UP} size={IconSize[40]} />
            <Typography className="text-center" variant="bold14">
              Drag &amp; drop files here
            </Typography>
            <Typography className="text-center" sx={classes.browseHint} variant="regular12">
              or{' '}
              <Box component="span" sx={classes.browseLink}>
                browse
              </Box>{' '}
              &mdash; {fileTypeName} &middot; up to 50&nbsp;MB
            </Typography>
          </div>
        </FormLabel>

        <Box className="flex justify-end items-center mt-32" sx={classes.buttonsContainer}>
          <Button
            data-testid={DocumentTestIds.DOCUMENTS_UPLOAD_CLEAR_BUTTON}
            disabled={!files.length || isUploading}
            variant="contained"
            onClick={onClearClick}
          >
            Clear
          </Button>

          <Button
            color="primary"
            data-testid={DocumentTestIds.DOCUMENTS_UPLOAD_UPLOAD_BUTTON}
            disabled={!files.length || isUploading}
            type="submit"
            variant="contained"
            onClick={onUploadFiles}
          >
            {uploadButtonText}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

UploadDocumentsModal.propTypes = {
  abortController: PropTypes.object.isRequired,
  customStyles: PropTypes.object,
  dialogId: PropTypes.string,
  documentTypeId: PropTypes.string,
  documentTypeName: PropTypes.string,
  formRef: PropTypes.shape({ current: PropTypes.object }),
  isModal: PropTypes.bool,
  isMultiple: PropTypes.bool,
  loanParams: PropTypes.object,
  missingUploadedFiles: PropTypes.array,
  updateMissingDocumentTypes: PropTypes.func,
  uploadButtonText: PropTypes.string,
  uploadFile: PropTypes.func,
  uploadFileAllowedFormats: PropTypes.array,
};

export default memo(UploadDocumentsModal);
